import {
  ChevronLeft,
  ChevronRight,
  QrCode,
  Search,
  UserCheck,
  UserPlus,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { StudentDetail } from "../components/StudentDetail";
import { StudentForm } from "../components/StudentForm";
import {
  Avatar,
  Button,
  EmptyState,
  IconButton,
  InlineNotice,
  PageHeader,
  StateBadge,
} from "../components/ui";
import { useApp } from "../state/AppContext";
import type { Student } from "../types";
import "../styles/students.css";

type ActivityFilter = "all" | "active" | "inactive";

const PAGE_SIZE = 25;

export function StudentsPage() {
  const { session, students, classes, settings } = useApp();
  const isAdmin = session?.role === "admin";
  const canViewOtherClasses =
    isAdmin || settings.permissions.teacherCanViewOtherClasses;
  const canViewPhones =
    isAdmin || settings.permissions.teacherCanViewGuardianPhone;
  const [query, setQuery] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>("all");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formStudent, setFormStudent] = useState<Student | null>(null);
  const [savedMessage, setSavedMessage] = useState("");

  const permittedClasses = useMemo(
    () =>
      canViewOtherClasses
        ? classes
        : classes.filter((schoolClass) => schoolClass.id === session?.assignedClassId),
    [canViewOtherClasses, classes, session?.assignedClassId],
  );

  const scopedStudents = useMemo(
    () =>
      canViewOtherClasses
        ? students
        : session?.assignedClassId
          ? students.filter((student) => student.classId === session.assignedClassId)
          : [],
    [canViewOtherClasses, session?.assignedClassId, students],
  );

  useEffect(() => {
    if (
      classFilter !== "all" &&
      !permittedClasses.some((schoolClass) => schoolClass.id === classFilter)
    ) {
      setClassFilter("all");
    }
  }, [classFilter, permittedClasses]);

  const filteredStudents = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("ms-MY");
    return scopedStudents
      .filter((student) => {
        if (classFilter !== "all" && student.classId !== classFilter) return false;
        if (activityFilter === "active" && !student.active) return false;
        if (activityFilter === "inactive" && student.active) return false;
        if (!normalizedQuery) return true;
        const searchable = [
          student.name,
          student.code,
          student.className,
          student.guardian.name,
        ]
          .join(" ")
          .toLocaleLowerCase("ms-MY");
        return searchable.includes(normalizedQuery);
      })
      .sort(
        (left, right) =>
          left.className.localeCompare(right.className, "ms-MY", { numeric: true }) ||
          left.name.localeCompare(right.name, "ms-MY"),
      );
  }, [activityFilter, classFilter, query, scopedStudents]);

  const pageCount = Math.max(1, Math.ceil(filteredStudents.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [activityFilter, classFilter, query]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  useEffect(() => {
    if (selectedId && !scopedStudents.some((student) => student.id === selectedId)) {
      setSelectedId(null);
    }
  }, [scopedStudents, selectedId]);

  useEffect(() => {
    if (!savedMessage) return;
    const timeout = window.setTimeout(() => setSavedMessage(""), 4200);
    return () => window.clearTimeout(timeout);
  }, [savedMessage]);

  const visibleStudents = filteredStudents.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );
  const selectedStudent =
    scopedStudents.find((student) => student.id === selectedId) ?? null;
  const activeCount = scopedStudents.filter((student) => student.active).length;
  const averageAttendance = scopedStudents.length
    ? Math.round(
        scopedStudents.reduce((total, student) => total + student.attendanceRate, 0) /
          scopedStudents.length,
      )
    : 0;

  const openNewStudent = () => {
    setFormStudent(null);
    setFormOpen(true);
  };

  const openEditStudent = (student: Student) => {
    if (!isAdmin) return;
    setSelectedId(null);
    setFormStudent(student);
    setFormOpen(true);
  };

  const rangeStart = filteredStudents.length ? (page - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd = Math.min(page * PAGE_SIZE, filteredStudents.length);

  return (
    <div className="students-page">
      <PageHeader
        eyebrow="Daftar murid"
        title="Murid"
        description={
          canViewOtherClasses
            ? "Urus profil, kelas, akses penjaga dan kad QR untuk seluruh sekolah."
            : `Paparan anda dihadkan kepada ${
                permittedClasses[0]?.name ?? "kelas yang ditugaskan"
              }.`
        }
        actions={
          isAdmin ? (
            <Button icon={UserPlus} onClick={openNewStudent}>
              Daftar murid
            </Button>
          ) : undefined
        }
      />

      {savedMessage && (
        <div className="students-page__notice" role="status">
          <InlineNotice tone="success" title="Profil berjaya disimpan">
            {savedMessage}
          </InlineNotice>
        </div>
      )}

      {!canViewOtherClasses && (
        <div className="students-page__scope">
          <InlineNotice tone="info" title="Akses guru kelas">
            Carian, jumlah dan profil di bawah hanya merangkumi murid kelas anda.
          </InlineNotice>
        </div>
      )}

      <section className="students-overview" aria-label="Ringkasan daftar murid">
        <article className="student-stat student-stat--primary">
          <span className="student-stat__icon">
            <Users size={20} />
          </span>
          <span>
            <small>Dalam akses anda</small>
            <strong>{scopedStudents.length}</strong>
            <em>{canViewOtherClasses ? "12 kelas sekolah" : permittedClasses[0]?.name}</em>
          </span>
        </article>
        <article className="student-stat">
          <span className="student-stat__icon student-stat__icon--success">
            <UserCheck size={20} />
          </span>
          <span>
            <small>Profil aktif</small>
            <strong>{activeCount}</strong>
            <em>{scopedStudents.length - activeCount} tidak aktif</em>
          </span>
        </article>
        <article className="student-stat">
          <span
            className="student-stat__ring"
            style={{ "--student-progress": `${averageAttendance * 3.6}deg` } as CSSProperties}
            aria-hidden="true"
          >
            <span>{averageAttendance}</span>
          </span>
          <span>
            <small>Purata kehadiran</small>
            <strong>{averageAttendance}%</strong>
            <em>Berdasarkan profil semasa</em>
          </span>
        </article>
      </section>

      <section className="panel students-directory">
        <div className="students-directory__tools">
          <div className="search-input students-directory__search">
            <Search size={18} />
            <label className="sr-only" htmlFor="student-search">
              Cari murid
            </label>
            <input
              id="student-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Cari nama atau kod murid…"
            />
          </div>
          <div className="students-directory__filters">
            <div className="form-field">
              <label htmlFor="student-class-filter">Kelas</label>
              <select
                id="student-class-filter"
                value={classFilter}
                onChange={(event) => setClassFilter(event.target.value)}
                disabled={permittedClasses.length <= 1}
              >
                <option value="all">Semua kelas</option>
                {permittedClasses.map((schoolClass) => (
                  <option key={schoolClass.id} value={schoolClass.id}>
                    {schoolClass.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="student-status-filter">Status profil</label>
              <select
                id="student-status-filter"
                value={activityFilter}
                onChange={(event) =>
                  setActivityFilter(event.target.value as ActivityFilter)
                }
              >
                <option value="all">Semua status</option>
                <option value="active">Aktif</option>
                <option value="inactive">Tidak aktif</option>
              </select>
            </div>
          </div>
        </div>

        <div className="students-directory__result-bar">
          <span>
            <strong>{filteredStudents.length}</strong> murid ditemui
          </span>
          {(query || classFilter !== "all" || activityFilter !== "all") && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setClassFilter("all");
                setActivityFilter("all");
              }}
            >
              Kosongkan penapis
            </button>
          )}
        </div>

        {visibleStudents.length ? (
          <>
            <div className="table-wrap desktop-only">
              <table className="data-table students-table">
                <thead>
                  <tr>
                    <th scope="col">Murid</th>
                    <th scope="col">Kelas</th>
                    <th scope="col">Penjaga utama</th>
                    <th scope="col">Kehadiran</th>
                    <th scope="col">Status</th>
                    <th scope="col">
                      <span className="sr-only">Tindakan</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleStudents.map((student, index) => (
                    <tr key={student.id}>
                      <td>
                        <button
                          type="button"
                          className="student-identity"
                          onClick={() => setSelectedId(student.id)}
                        >
                          <Avatar
                            initials={student.initials}
                            size="md"
                            tone={index}
                            label={`Avatar ${student.name}`}
                          />
                          <span>
                            <strong>{student.name}</strong>
                            <small>{student.code}</small>
                          </span>
                        </button>
                      </td>
                      <td>
                        <span className="student-class-cell">
                          <strong>{student.className}</strong>
                          <small>
                            {classes.find((item) => item.id === student.classId)?.room}
                          </small>
                        </span>
                      </td>
                      <td>
                        <span className="student-guardian-cell">
                          <strong>{student.guardian.name}</strong>
                          <small>
                            {canViewPhones
                              ? student.guardian.phone
                              : "Nombor dilindungi"}
                          </small>
                        </span>
                      </td>
                      <td>
                        <span className="student-rate">
                          <span>
                            <i style={{ width: `${student.attendanceRate}%` }} />
                          </span>
                          <strong>{student.attendanceRate}%</strong>
                        </span>
                      </td>
                      <td>
                        <StateBadge state={student.active ? "active" : "inactive"} />
                      </td>
                      <td className="students-table__action">
                        <IconButton
                          icon={QrCode}
                          label={`Lihat profil dan QR ${student.name}`}
                          onClick={() => setSelectedId(student.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="student-mobile-list mobile-only">
              {visibleStudents.map((student, index) => (
                <button
                  key={student.id}
                  type="button"
                  className="student-mobile-row"
                  onClick={() => setSelectedId(student.id)}
                >
                  <Avatar
                    initials={student.initials}
                    size="md"
                    tone={index}
                    label={`Avatar ${student.name}`}
                  />
                  <span className="student-mobile-row__main">
                    <strong>{student.name}</strong>
                    <small>
                      {student.className} · {student.code}
                    </small>
                    <span className="student-mobile-row__rate">
                      <i>
                        <b style={{ width: `${student.attendanceRate}%` }} />
                      </i>
                      {student.attendanceRate}% kehadiran
                    </span>
                  </span>
                  <span className="student-mobile-row__end">
                    <StateBadge state={student.active ? "active" : "inactive"} />
                    <QrCode size={18} />
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <EmptyState
            icon={Search}
            title="Tiada murid ditemui"
            message={
              scopedStudents.length
                ? "Cuba nama, kod atau gabungan penapis yang lain."
                : "Tiada murid dalam skop kelas yang diberikan kepada akaun ini."
            }
            action={
              scopedStudents.length ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setQuery("");
                    setClassFilter("all");
                    setActivityFilter("all");
                  }}
                >
                  Kosongkan penapis
                </Button>
              ) : undefined
            }
          />
        )}

        {filteredStudents.length > PAGE_SIZE && (
          <nav className="students-pagination" aria-label="Halaman senarai murid">
            <span>
              Menunjukkan {rangeStart}–{rangeEnd} daripada {filteredStudents.length}
            </span>
            <div>
              <Button
                variant="secondary"
                size="compact"
                icon={ChevronLeft}
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Sebelum
              </Button>
              <span className="students-pagination__page">
                {page} / {pageCount}
              </span>
              <Button
                variant="secondary"
                size="compact"
                iconAfter={ChevronRight}
                disabled={page >= pageCount}
                onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              >
                Seterusnya
              </Button>
            </div>
          </nav>
        )}
      </section>

      <StudentDetail
        open={Boolean(selectedStudent)}
        student={selectedStudent}
        onClose={() => setSelectedId(null)}
        onEdit={openEditStudent}
      />
      <StudentForm
        open={formOpen}
        student={formStudent}
        onClose={() => {
          setFormOpen(false);
          setFormStudent(null);
        }}
        onSaved={(saved) => {
          setSavedMessage(
            `${saved.name} (${saved.code}) kini tersedia dalam daftar murid.`,
          );
        }}
      />
    </div>
  );
}

